import { BaseModel, type RelationConfig } from './base';

export enum GiftCardGiftDeliveredType {
  undelivered = 0,
  delivered = 1,
  failed = 2,
}

export const GiftCardGiftDeliveredTypeLabels: Record<GiftCardGiftDeliveredType, string> = {
  [GiftCardGiftDeliveredType.undelivered]: 'giftCard.undelivered',
  [GiftCardGiftDeliveredType.delivered]: 'general.delivered',
  [GiftCardGiftDeliveredType.failed]: 'giftCard.deliveryFailed',
};

export class GiftCardGift extends BaseModel {
  delivered!: GiftCardGiftDeliveredType;
}

export enum GiftCardTransactionType {
  manual = 0,
  purchase = 1,
  payment = 2,
  refund = 3,
}

export class GiftCardTransaction extends BaseModel {
  idGiftCardTransaction!: number;
  type!: GiftCardTransactionType;
  amount!: number;
  date!: number;

  // order relation will be added when Order is migrated
}

export class GiftCard extends BaseModel {
  static modelName(): string {
    return 'giftCard'
  }

  idGiftCard!: number;
  number!: string;
  customNumber!: string;
  displayName!: string;
  balance!: number;
  idGiftCardImage!: number | null;
  gift!: GiftCardGift | null;
  transactions!: GiftCardTransaction[];

  static override relations(): RelationConfig {
    return {
      transactions: { type: GiftCardTransaction },
      gift: { type: GiftCardGift },
    };
  }

  override objectID(): string | null {
    return this.idGiftCard?.toString() ?? null;
  }

  displayNumber(): string {
    return (this.customNumber && this.customNumber.length > 0) ? this.customNumber : this.number;
  }

  matchesQuery(query: string): boolean {
    if (this.displayNumber().includes(query)) return true;
    if (this.displayName?.includes(query)) return true;
    return false;
  }

  get isGift(): boolean {
    return !!this.gift;
  }

  get isGiftDelivered(): boolean {
    return !!this.gift && this.gift.delivered === GiftCardGiftDeliveredType.delivered;
  }

  get isGiftFailed(): boolean {
    return !!this.gift && this.gift.delivered === GiftCardGiftDeliveredType.failed;
  }

  get isGiftUndelivered(): boolean {
    return !!this.gift && this.gift.delivered === GiftCardGiftDeliveredType.undelivered;
  }

  /**
   * The API exposes the gift card's ledger but not its original value. The
   * first positive funding transaction is the creation amount; later positive
   * refunds must not inflate it.
   */
  get originalAmount(): number | null {
    const fundingTypes = new Set([
      GiftCardTransactionType.manual,
      GiftCardTransactionType.purchase,
    ]);

    const transaction = (this.transactions ?? [])
      .filter((item) => fundingTypes.has(Number(item.type) as GiftCardTransactionType) && Number(item.amount) > 0)
      .sort((left, right) => {
        const dateDifference = Number(left.date) - Number(right.date);
        return dateDifference || Number(left.idGiftCardTransaction) - Number(right.idGiftCardTransaction);
      })[0];

    return transaction ? Number(transaction.amount) : null;
  }
}
